import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@frontend-platform/ui-lib/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Ban, MoreHorizontal, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { cancelCalibrationRun, deleteCalibrationRun } from '../api/api';
import { QK } from '../lib/query-keys';
import { isInProgressStatus } from '../lib/status';
import type { CalibrationRun } from '../lib/types';

interface RunRowActionsProps {
  run: CalibrationRun;
}

type ConfirmMode = 'delete' | 'cancel';

// Mirrors aistudio's TableView.vue getOptionsList(row) + the single
// delete/cancel confirm modal (mode-switched) in RunList.vue. In-progress
// runs offer "Cancel", everything else offers "Delete"; both prompt for
// confirmation before firing the mutation.
export function RunRowActions({ run }: RunRowActionsProps) {
  const inProgress = isInProgressStatus(run.status);
  const [confirmMode, setConfirmMode] = useState<ConfirmMode | null>(null);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: () => deleteCalibrationRun(run.runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK.calibration.runs.all });
      toast.success(`"${run.runName}" deleted`);
      setConfirmMode(null);
    },
  });

  // Cancel keeps the row in the list — the backend flips it to CANCELLED
  // (releasing the parallel-run slot and interrupting DS) rather than
  // removing it, so a refetch is enough; no local row removal like delete.
  const cancelMutation = useMutation({
    mutationFn: () => cancelCalibrationRun(run.runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK.calibration.runs.all });
      toast.success(`"${run.runName}" canceled`);
      setConfirmMode(null);
    },
  });

  const activeMutation = confirmMode === 'cancel' ? cancelMutation : deleteMutation;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={`Actions for ${run.runName}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44 p-1">
          <DropdownMenuLabel className="px-3 py-2 font-medium text-muted-foreground text-xs">
            Actions
          </DropdownMenuLabel>
          {inProgress ? (
            <DropdownMenuItem
              className="cursor-pointer gap-2 px-3 py-2 text-foreground text-sm"
              onSelect={() => setConfirmMode('cancel')}
            >
              <Ban className="h-4 w-4 text-muted-foreground" />
              Cancel
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              className="cursor-pointer gap-2 px-3 py-2 text-foreground text-sm"
              onSelect={() => setConfirmMode('delete')}
            >
              <Trash2 className="h-4 w-4 text-muted-foreground" />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete/cancel confirmation — mirrors RunList.vue's single
          deleteModal (mode-switched) layout: title, single-line body
          referencing the run name, right-aligned Cancel + confirm buttons. */}
      <Dialog open={confirmMode !== null} onOpenChange={(open) => !open && setConfirmMode(null)}>
        <DialogContent className="max-w-md gap-2 p-6">
          <DialogHeader className="space-y-1 text-left sm:text-left">
            <DialogTitle className="text-left font-medium text-base text-foreground">
              {confirmMode === 'cancel' ? 'Cancel this run?' : 'Delete this run?'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">
            "{run.runName}" will be {confirmMode === 'cancel' ? 'canceled' : 'permanently removed'}.
            This action cannot be undone.
          </p>
          <DialogFooter className="mt-4 flex justify-end gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setConfirmMode(null)}
              disabled={activeMutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={() => activeMutation.mutate()} disabled={activeMutation.isPending}>
              {confirmMode === 'cancel'
                ? activeMutation.isPending
                  ? 'Canceling…'
                  : 'Cancel run'
                : activeMutation.isPending
                  ? 'Deleting…'
                  : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
